import { type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { ehLargo, useTour } from "../../estado/tour.js";
import { Icone } from "../Icone.js";
import { PASSOS, TOTAL_DE_ETAPAS, type LadoDoPostit, type PassoDoTour } from "./passos.js";

/**
 * O tour ao vivo (ADR 041): um post-it amarelo com seta desenhada à mão,
 * apontando para o elemento real da tela.
 *
 * Não há véu: a tela continua visível, mas só o que o passo pede responde ao
 * toque (`useTrava`); o resto faz o lugar certo tremer. Os passos de ação só
 * avançam quando a pessoa faz a ação no próprio alvo. Se o alvo não está na
 * tela (ela navegou para outro lugar), o post-it vira um lembrete no canto,
 * com "voltar ao tour" e "pular tour": nunca some sem deixar saída.
 *
 * A geometria é a do handoff, em px da janela: post-it de 300px, afastado
 * 124px do alvo; seta quadrática
 * com ponta de duas linhas. Posição vai por custom property via CSSOM (a CSP
 * não tem `style-src-attr`), e o desenho é geometria de SVG, não estilo.
 */

/**
 * 300px é a largura do handoff. Abaixo de 1440px de janela a margem livre ao
 * lado da questão é mais estreita que isso, e o post-it acabava em cima das
 * alternativas; ali ele encolhe.
 */
function larguraDoPostit(janela: number): number {
  if (janela < 1320) return 200;
  return janela < 1440 ? 250 : 300;
}
/** Altura até a primeira medida do post-it; depois vale a medida. */
const ALTURA_INICIAL = 190;
const MARGEM = 24;

type Retangulo = { x: number; y: number; w: number; h: number };

export function TourGuiado() {
  const { passo } = useTour();
  const [largo, setLargo] = useState(ehLargo);

  useEffect(() => {
    const m = window.matchMedia("(min-width: 48rem)");
    const mudar = () => setLargo(m.matches);
    m.addEventListener("change", mudar);
    return () => m.removeEventListener("change", mudar);
  }, []);

  if (!largo) return null;
  if (passo === null) return <ConviteDoTour />;
  return <PostitDoPasso indice={passo} passo={PASSOS[passo]!} />;
}

/**
 * A pergunta antes do tour, para conta que nunca o viu. É ela que decide: o
 * tour troca a tela pelo ensaio, e isso não acontece sem a pessoa pedir.
 */
function ConviteDoTour() {
  const { convite, refazer, recusar } = useTour();
  const dialogo = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialogo.current;
    if (!d) return;
    if (convite && !d.open) d.showModal();
    if (!convite && d.open) d.close();
  }, [convite]);

  if (!convite) return null;
  return (
    <dialog
      ref={dialogo}
      className="folha-dialogo tour-convite"
      aria-labelledby="tour-convite-titulo"
      onCancel={(e) => {
        e.preventDefault();
        recusar();
      }}
    >
      <h2 className="t-cartao" id="tour-convite-titulo">
        Quer um tour pelo Grifo?
      </h2>
      <p className="t-corpo">
        São seis etapas, uns dois minutos: criar uma trilha, importar questões, responder, a meta do dia e o Revisar.
      </p>
      <p className="t-ajuda">
        É um ensaio: o tour usa uma conta de mentira, e nada do que você fizer nele muda a sua, inclusive nem precisa digitar nem nada, apenas clicar no que for pedido. Dá para pular a
        qualquer momento, e refazer depois pela lateral.
      </p>
      <div className="tour-convite__acoes">
        <button type="button" className="botao" onClick={refazer}>
          Fazer o tour
        </button>
        <button type="button" className="botao botao--secundario" onClick={recusar}>
          Agora não
        </button>
      </div>
    </dialog>
  );
}

/**
 * O alvo do passo não está nesta tela: o post-it vira um lembrete no canto.
 * Espera um instante antes de aparecer — entre uma tela e outra o alvo some por
 * frações de segundo, e o lembrete piscaria a cada navegação.
 */
function TourEmPausa({ passo }: { passo: PassoDoTour }) {
  const { pular } = useTour();
  const navegar = useNavigate();
  const [mostrar, setMostrar] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setMostrar(true), 1200);
    return () => window.clearTimeout(t);
  }, []);
  if (!mostrar) return null;
  return (
    <aside className="tour-pausa" aria-label="Tour em pausa">
      <span>
        Tour em pausa · {passo.etapa} de {TOTAL_DE_ETAPAS}
      </span>
      <button type="button" className="tour-postit__entendi tour-postit__por-mim" onClick={() => navegar(passo.tela)}>
        voltar ao tour →
      </button>
      <button type="button" className="tour-postit__pular" onClick={pular}>
        pular tour
      </button>
    </aside>
  );
}

function PostitDoPasso({ indice, passo }: { indice: number; passo: PassoDoTour }) {
  const { dados, entendi, pular } = useTour();
  const { pathname, search } = useLocation();
  const alvo = useRetanguloDoAlvo(passo.alvo, `${pathname}${search}`, passo.reserva?.at(-1));
  const postit = useRef<HTMLElement>(null);
  const janela = useJanela();

  const [altura, setAltura] = useState(ALTURA_INICIAL);
  useLayoutEffect(() => {
    const h = postit.current?.offsetHeight;
    if (h && h !== altura) setAltura(h);
  });

  // A tela ao redor muda sem o alvo mudar (um link que chega com o painel, um
  // botão que aparece depois da resposta): a posição se refaz de tempos em tempos.
  const [, setVolta] = useState(0);
  useEffect(() => {
    let quadro = 0;
    const refazer = () => {
      cancelAnimationFrame(quadro);
      quadro = requestAnimationFrame(() => setVolta((v) => v + 1));
    };
    const observador = new MutationObserver((mudancas) => {
      // O que o próprio tour escreve (posição, traço) não conta.
      if (mudancas.every((m) => (m.target as Element).closest?.(".tour-postit, .tour-desenho"))) return;
      refazer();
    });
    observador.observe(document.body, { childList: true, subtree: true });
    const t = window.setInterval(refazer, 500);
    return () => {
      observador.disconnect();
      window.clearInterval(t);
      cancelAnimationFrame(quadro);
    };
  }, []);

  // Além do alvo, o post-it evita tudo que se toca na tela — o "Próximo item"
  // ficava embaixo dele e só dava para clicar rolando a página.
  const lugar = alvo ? posicionar(alvo, passo, janela, altura, tocaveis()) : null;

  useLayoutEffect(() => {
    const el = postit.current;
    if (!el || !lugar) return;
    el.style.setProperty("--tour-largura", `${larguraDoPostit(janela.w)}px`);
    el.style.setProperty("--tour-x", `${Math.round(lugar.x)}px`);
    el.style.setProperty("--tour-y", `${Math.round(lugar.y)}px`);
  }, [lugar?.x, lugar?.y, janela.w]);

  const visivel = Boolean(lugar);
  useTrava(passo, visivel, postit);
  const linhas = passo.texto(dados);
  const [semAlvo, setSemAlvo] = useState(false);
  useEffect(() => setSemAlvo(!alvo), [alvo]);

  return (
    <>
      {semAlvo && <TourEmPausa key={indice} passo={passo} />}
      {alvo && lugar && (
        <Desenho
          // Remontar a cada passo é o que faz o traço se desenhar de novo.
          key={`${indice}-${pathname}`}
          lugar={lugar}
          janela={janela}
        />
      )}
      <aside
        ref={postit}
        className={[
          "tour-postit",
          indice % 2 ? "tour-postit--impar" : "tour-postit--par",
          visivel ? "tour-postit--visivel" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label="Tour de primeiro uso"
        aria-hidden={!visivel}
        inert={!visivel}
      >
        <div className="tour-postit__topo">
          <span>
            {passo.etapa} de {TOTAL_DE_ETAPAS} · {passo.nomeDaEtapa} ·{" "}
            <abbr className="tour-postit__ensaio" title="O tour usa uma conta de mentira: nada disto muda a sua.">
              ensaio
            </abbr>
          </span>
          <span className="tour-postit__bolinhas" aria-hidden="true">
            {Array.from({ length: TOTAL_DE_ETAPAS }, (_, i) => (
              <span
                key={i}
                className={i < passo.etapa ? "tour-postit__bolinha tour-postit__bolinha--cheia" : "tour-postit__bolinha"}
              />
            ))}
          </span>
        </div>
        <div className="tour-postit__corpo" aria-live="polite">
          {linhas.map((l) => (
            <span key={l}>{comDestaque(l)}</span>
          ))}
        </div>
        <div className="tour-postit__rodape">
          {passo.tipo === "info" ? (
            <button type="button" className="tour-postit__entendi" onClick={entendi}>
              {passo.ultimo ? "terminar o tour" : "entendi →"}
            </button>
          ) : (
            <span className="tour-postit__vez">
              <Icone glifo="mao" papel="acao" tamanho="p" />
              sua vez ·
              <button type="button" className="tour-postit__entendi tour-postit__por-mim" onClick={() => void fazerPorMim(passo)}>
                faça por mim →
              </button>
            </span>
          )}
          <button type="button" className="tour-postit__pular" onClick={pular}>
            pular tour
          </button>
        </div>
      </aside>
    </>
  );
}

/**
 * O "faça por mim" dos passos de ação: toca, em ordem, o que a pessoa tocaria.
 * Campo recebe foco — é o foco que o preenche no ensaio. Entre um toque e o
 * próximo espera a tela reagir (o Confirmar só acende depois da escolha).
 */
/**
 * Enquanto o post-it está na tela, só se toca o que o passo pede: o alvo e a
 * `reserva` nos passos de ação, o próprio post-it em todos (o "entendi", o
 * "faça por mim" e o "pular tour", que é a saída). Um toque em qualquer outro
 * lugar não chega à página, e o lugar certo chacoalha para dizer onde é.
 *
 * Fica na captura do `document`, antes de qualquer manipulador da tela. O
 * clique que o teclado dispara (Enter num botão ou link) passa pelo mesmo
 * filtro. Nos passos de informação o teclado também trava, salvo Tab e o que
 * se aperta dentro do post-it; nos de ação não, porque ali os atalhos (letra
 * da alternativa, Enter) são um jeito de cumprir o passo.
 */
function useTrava(passo: PassoDoTour, ativa: boolean, postit: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!ativa) return;
    const permitidos = [
      ".tour-postit",
      ...(passo.tipo === "acao" ? [`[data-tour="${passo.alvo}"]`, ...(passo.reserva ?? [])] : []),
      ...(passo.livre ?? []),
    ].join(", ");
    const pode = (alvo: EventTarget | null) => alvo instanceof Element && alvo.closest(permitidos) !== null;

    const chamar = () => {
      const el =
        passo.tipo === "info"
          ? postit.current?.querySelector<HTMLElement>(".tour-postit__entendi")
          : document.querySelector<HTMLElement>(`[data-tour="${passo.alvo}"]`);
      if (!el) return;
      // Tirar e pôr de volta reinicia o balanço a cada toque errado.
      el.classList.remove("tour-chamar");
      void el.offsetWidth;
      el.classList.add("tour-chamar");
      el.addEventListener("animationend", () => el.classList.remove("tour-chamar"), { once: true });
    };

    const barrar = (e: Event) => {
      if (pode(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "pointerdown" || e.type === "keydown") chamar();
    };
    const barrarTecla = (e: KeyboardEvent) => {
      if (passo.tipo !== "info" || e.key === "Tab" || pode(e.target)) return;
      barrar(e);
    };

    const eventos = ["pointerdown", "mousedown", "click", "dblclick", "contextmenu"] as const;
    for (const t of eventos) document.addEventListener(t, barrar, { capture: true });
    window.addEventListener("keydown", barrarTecla, { capture: true });
    return () => {
      for (const t of eventos) document.removeEventListener(t, barrar, { capture: true });
      window.removeEventListener("keydown", barrarTecla, { capture: true });
    };
  }, [passo, ativa, postit]);
}

/** `**trecho**` no texto de um passo sai em negrito. */
function comDestaque(linha: string): ReactNode[] {
  return linha.split(/\*\*(.+?)\*\*/g).map((parte, i) => (i % 2 ? <strong key={i}>{parte}</strong> : parte));
}

async function fazerPorMim(passo: PassoDoTour): Promise<void> {
  const seletores = passo.reserva ?? [`[data-tour="${passo.alvo}"]`];
  for (const [i, seletor] of seletores.entries()) {
    if (i > 0) await new Promise((ok) => setTimeout(ok, 120));
    const el = document.querySelector<HTMLElement>(seletor);
    if (!el) return;
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.focus();
    else el.click();
  }
}

/* ------------------------------------------------------------ medida --- */

function useJanela(): { w: number; h: number } {
  const [j, setJ] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const mudar = () => setJ({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", mudar);
    return () => window.removeEventListener("resize", mudar);
  }, []);
  return j;
}

/**
 * O retângulo do alvo na janela, remedido quando a tela muda, rola ou muda
 * de tamanho — e a cada fração de segundo, porque o alvo pode nascer depois
 * de uma resposta do servidor (a conferência, o fim de sessão). Alvo ausente
 * ou sem tamanho é `null`: o post-it some até ele voltar.
 */
function useRetanguloDoAlvo(alvo: string, endereco: string, botao?: string): Retangulo | null {
  const [r, setR] = useState<Retangulo | null>(null);
  const rolou = useRef(false);

  useEffect(() => {
    rolou.current = false;
    let quadro = 0;

    const medir = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${alvo}"]`);
      const novo = el ? retanguloDe(el, alvo) : null;
      // Alvo fora da janela: rola até ele uma vez por passo, e não briga com
      // quem rolar de volta depois.
      if (el && novo && !rolou.current) {
        rolou.current = true;
        const suave = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        // O botão que executa o passo vem junto: com a explicação longa, o
        // "Próximo item" ficava abaixo da dobra.
        const b = botao ? document.querySelector<HTMLElement>(botao) : null;
        const fb = b?.getBoundingClientRect();
        if (b && fb && fb.bottom > window.innerHeight && fb.bottom - novo.y < window.innerHeight - 48) {
          b.scrollIntoView({ block: "end", behavior: suave ? "smooth" : "auto" });
        } else if (novo.y < 0 || novo.y + novo.h > window.innerHeight) {
          el.scrollIntoView({ block: "center", behavior: suave ? "smooth" : "auto" });
        }
      }
      setR((atual) => (mesmo(atual, novo) ? atual : novo));
    };
    const agendar = () => {
      cancelAnimationFrame(quadro);
      quadro = requestAnimationFrame(medir);
    };

    agendar();
    const relogio = window.setInterval(medir, 300);
    window.addEventListener("scroll", agendar, { capture: true, passive: true });
    window.addEventListener("resize", agendar);
    return () => {
      cancelAnimationFrame(quadro);
      window.clearInterval(relogio);
      window.removeEventListener("scroll", agendar, { capture: true });
      window.removeEventListener("resize", agendar);
    };
  }, [alvo, endereco, botao]);

  return r;
}

/**
 * Estes alvos medem a união dos filhos, não a caixa inteira: o item da lateral
 * (ícone + rótulo, não o link até a borda) e as alternativas (as palavras, não
 * a linha da coluna toda). Sem isso a seta apontaria para o meio da folha.
 */
const PELA_UNIAO_DOS_FILHOS = new Set(["navRevisar", "alts"]);

function retanguloDe(el: HTMLElement, alvo: string): Retangulo | null {
  let q: { left: number; top: number; right: number; bottom: number } = el.getBoundingClientRect();
  if (PELA_UNIAO_DOS_FILHOS.has(alvo) && el.children.length > 0) {
    let L = Infinity;
    let T = Infinity;
    let R = -Infinity;
    let B = -Infinity;
    for (const filho of Array.from(el.children)) {
      const c = filho.getBoundingClientRect();
      if (!c.width) continue;
      L = Math.min(L, c.left);
      T = Math.min(T, c.top);
      R = Math.max(R, c.right);
      B = Math.max(B, c.bottom);
    }
    if (R > L) q = { left: L, top: T, right: R, bottom: B };
  }
  const w = q.right - q.left;
  const h = q.bottom - q.top;
  if (w <= 0 || h <= 0) return null;
  return { x: Math.round(q.left), y: Math.round(q.top), w: Math.round(w), h: Math.round(h) };
}

function mesmo(a: Retangulo | null, b: Retangulo | null): boolean {
  if (!a || !b) return a === b;
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/* --------------------------------------------------------- geometria --- */

type Lugar = { x: number; y: number; seta: [number, number, number, number] };

/**
 * O alvo como elipse, só para a seta: ela chega até a borda dele, com folga
 * pequena. Era a elipse do círculo à mão, que saiu quando o tremor passou a
 * chamar a atenção (`useTrava`); larga como era, a ponta parava longe do alvo.
 */
function elipse(r: Retangulo) {
  return { cx: r.x + r.w / 2, cy: r.y + r.h / 2, rx: r.w / 2 + 4, ry: r.h / 2 + 2 };
}

/**
 * O lado pedido pelo passo, se couber. Não cabendo, o post-it preso na borda
 * da janela caía em cima do próprio alvo — a lacuna, a alternativa, o botão —
 * e a pessoa não conseguia fazer o que ele pedia. Então tenta o lado oposto e
 * depois embaixo, e fica no primeiro que não cobre o alvo nem o botão que
 * executa o passo.
 */
function posicionar(
  r: Retangulo,
  passo: PassoDoTour,
  janela: { w: number; h: number },
  altura: number,
  tocaveis: Retangulo[],
): Lugar {
  const opostos: Record<LadoDoPostit, LadoDoPostit[]> = {
    right: ["left", "below", "belowR"],
    left: ["right", "below", "belowR"],
    below: ["belowR", "right", "left"],
    belowR: ["below", "right", "left"],
  };
  // O alvo em si, com folga — não a elipse do círculo, que é larga demais e
  // descartava a margem ao lado da questão.
  const alvo = { x: r.x - 12, y: r.y - 12, w: r.w + 24, h: r.h + 24 };
  // Além da posição do handoff, o post-it procura mais longe — para o lado,
  // para cima e para baixo — e fica na que não cobre nada tocável; entre as
  // livres, a mais perto do alvo. Cobrir o alvo pesa mil vezes mais que cobrir
  // outra coisa, e a distância só desempata.
  const passoY = altura / 2 + 40;
  const dys = [0, -passoY, passoY, -2 * passoY, 2 * passoY];
  const dxs = [0, 150, 300, 450, 600, 750, 900];
  let melhor: Lugar | null = null;
  let menor = Infinity;
  for (const [ordem, lado] of [passo.lado, ...opostos[passo.lado]].entries()) {
    const lateral = lado === "right" || lado === "left";
    for (const d of lateral ? dys : [0]) {
      for (const dx of lateral ? dxs : [0]) {
        const dy = (lado === passo.lado ? (passo.dy ?? 0) : 0) + d;
        const lugar = posicionarDoLado(r, lado, dy, janela, altura, lado === "left" ? -dx : dx);
        const caixa = { x: lugar.x, y: lugar.y, w: larguraDoPostit(janela.w), h: altura };
        // Encostar num tocável custa caro mesmo que por pouco: um link com
        // metade coberta já é um link que não se acha.
        const cobre =
          1000 * area(caixa, alvo) + tocaveis.reduce((t, q) => t + (area(caixa, q) > 0 ? 2000 + area(caixa, q) : 0), 0);
        const longe = Math.abs(dx) + Math.abs(d) + ordem * 200;
        const custo = cobre * 10 + longe;
        if (custo < menor) {
          menor = custo;
          melhor = lugar;
        }
      }
    }
  }
  return melhor ?? posicionarDoLado(r, passo.lado, passo.dy ?? 0, janela, altura, 0);
}

function area(a: Retangulo, b: Retangulo): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

/** O que se toca na janela agora, fora o próprio tour. */
function tocaveis(): Retangulo[] {
  const lista: Retangulo[] = [];
  const els = document.querySelectorAll<HTMLElement>(
    "button, a[href], input:not([type=hidden]), textarea, select, [role=radio], [role=button]",
  );
  for (const el of Array.from(els)) {
    if (el.closest(".tour-postit, .tour-pausa, .tour-convite")) continue;
    const q = el.getBoundingClientRect();
    if (q.width <= 0 || q.height <= 0 || q.bottom < 0 || q.top > window.innerHeight) continue;
    lista.push({ x: q.left, y: q.top, w: q.width, h: q.height });
  }
  return lista;
}


function posicionarDoLado(
  r: Retangulo,
  lado: LadoDoPostit,
  dy: number,
  janela: { w: number; h: number },
  altura: number,
  dx: number,
): Lugar {
  const LARGURA = larguraDoPostit(janela.w);
  const { cx, cy, rx, ry } = elipse(r);
  const entre = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
  let x: number;
  let y: number;
  if (lado === "right") {
    x = r.x + r.w + 124 + dx;
    y = cy - 60 + dy;
  } else if (lado === "left") {
    x = r.x - 124 - LARGURA + dx;
    y = cy - 60 + dy;
  } else if (lado === "belowR") {
    x = r.x + r.w - LARGURA - 40;
    y = r.y + r.h + 104;
  } else {
    x = r.x + 40;
    y = r.y + r.h + 94;
  }
  x = entre(x, MARGEM, Math.max(MARGEM, janela.w - LARGURA - MARGEM));
  y = entre(y, MARGEM, Math.max(MARGEM, janela.h - altura - MARGEM));

  // A seta parte de onde o post-it **ficou**, não do lado pedido: preso na
  // borda da janela ou empurrado para longe de um tocável, ele pode ter ido
  // parar embaixo ou do outro lado, e a seta cruzava o alvo.
  let seta: Lugar["seta"];
  const saida = Math.round(entre(cy, y + 24, y + altura - 24));
  if (x >= cx + rx) seta = [x - 8, saida, Math.round(cx + rx + 6), Math.round(cy)];
  else if (x + LARGURA <= cx - rx) seta = [x + LARGURA + 8, saida, Math.round(cx - rx - 6), Math.round(cy)];
  else {
    const fimX = Math.round(entre(x + LARGURA / 2, cx - rx * 0.55, cx + rx * 0.55));
    const inicioX = Math.round(entre(fimX, x + 24, x + LARGURA - 24));
    seta =
      y >= cy
        ? [inicioX, y - 8, fimX, Math.round(cy + ry + 6)]
        : [inicioX, y + altura + 8, fimX, Math.round(cy - ry - 6)];
  }
  return { x, y, seta };
}

function caminhosDaSeta([sx, sy, ex, ey]: Lugar["seta"]): { corpo: string; ponta: string } {
  const mx = (sx + ex) / 2;
  const my = (sy + ey) / 2;
  const dx = ex - sx;
  const dy = ey - sy;
  // Controle deslocado na perpendicular, 22% do comprimento.
  const qx = mx - dy * 0.22;
  const qy = my + dx * 0.22;
  const angulo = Math.atan2(ey - qy, ex - qx);
  const lado = 15;
  const ponta =
    `M${(ex - lado * Math.cos(angulo - 0.5)).toFixed(1)} ${(ey - lado * Math.sin(angulo - 0.5)).toFixed(1)}` +
    ` L${ex} ${ey}` +
    ` L${(ex - lado * Math.cos(angulo + 0.5)).toFixed(1)} ${(ey - lado * Math.sin(angulo + 0.5)).toFixed(1)}`;
  return { corpo: `M${sx} ${sy} Q${qx.toFixed(1)} ${qy.toFixed(1)} ${ex} ${ey}`, ponta };
}

function Desenho({ lugar, janela }: { lugar: Lugar; janela: { w: number; h: number } }) {
  const seta = caminhosDaSeta(lugar.seta);
  return (
    <svg
      className="tour-desenho"
      width={janela.w}
      height={janela.h}
      viewBox={`0 0 ${janela.w} ${janela.h}`}
      aria-hidden="true"
      focusable="false"
    >
      <path className="tour-desenho__traco tour-desenho__seta" pathLength={1} d={seta.corpo} />
      <path className="tour-desenho__traco tour-desenho__ponta" pathLength={1} d={seta.ponta} />
    </svg>
  );
}
